import { useCallback, useEffect, useRef } from 'react';
import { useCourseProgress } from '@/presentation/hook/useCourseProgress';
import { FULL_PROGRESS_PERCENTAGE } from '@/domain/model/courseProgressUtils';
import { shouldReportProgress } from '@/presentation/util/progressTracking';

interface UseCourseProgressReporterOptions {
  courseId: string | undefined;
  isEnabled: boolean;
}

export const useCourseProgressReporter = ({
  courseId,
  isEnabled,
}: UseCourseProgressReporterOptions) => {
  const { saveProgress } = useCourseProgress();
  const reportedPercentageRef = useRef<number | null>(null);
  const watchedPercentageRef = useRef<number | null>(null);
  const lastReportAtRef = useRef(0);

  useEffect(() => {
    reportedPercentageRef.current = null;
    watchedPercentageRef.current = null;
    lastReportAtRef.current = 0;

    return () => {
      const watched = watchedPercentageRef.current;
      const reported = reportedPercentageRef.current;
      if (!courseId || !isEnabled || watched === null || reported === null) return;
      if (watched <= reported) return;
      saveProgress(courseId, watched);
    };
  }, [courseId, isEnabled, saveProgress]);

  const reportProgress = useCallback(
    (percentage: number) => {
      if (!courseId || !isEnabled) return;
      watchedPercentageRef.current = percentage;
      if (reportedPercentageRef.current === null) {
        reportedPercentageRef.current = percentage;
        return;
      }
      const elapsedMs = Date.now() - lastReportAtRef.current;
      if (!shouldReportProgress(reportedPercentageRef.current, percentage, elapsedMs)) return;
      reportedPercentageRef.current = percentage;
      lastReportAtRef.current = Date.now();
      saveProgress(courseId, percentage);
    },
    [courseId, isEnabled, saveProgress],
  );

  const reportCompleted = useCallback(() => {
    if (!courseId || !isEnabled) return;
    watchedPercentageRef.current = FULL_PROGRESS_PERCENTAGE;
    reportedPercentageRef.current = FULL_PROGRESS_PERCENTAGE;
    lastReportAtRef.current = Date.now();
    saveProgress(courseId, FULL_PROGRESS_PERCENTAGE);
  }, [courseId, isEnabled, saveProgress]);

  return { reportProgress, reportCompleted };
};
